import { Suspense, useEffect, useLayoutEffect, useRef, type ReactElement, type ReactNode, type Ref } from 'react';
import { UNSAFE_LocationContext as LocationContext, useLocation, useNavigationType, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion, useIsPresent } from 'framer-motion';

import { pageVariants } from '@/lib/motion';

/*
 * Route-change wrapper — the exact mechanism from ui-ux.md §4 "Route
 * transitions — the actual mechanism":
 *
 *  - <Suspense> wraps <AnimatePresence>, never the other way around. A lazy
 *    chunk that suspends swaps the whole content area to the skeleton and
 *    back; it can no longer leave the entering page stuck at its `initial`
 *    opacity (the old mode="wait" regression: blank content, nav visible,
 *    and the screen's on-mount data query never firing).
 *  - mode="popLayout" — NEVER "wait". The entering route takes layout space
 *    immediately, so a slow chunk cannot block the enter, and the exiting
 *    route is popped out of flow to finish its exit on top.
 *  - A 2-entry pathname → outlet cache keeps the previous route's rendered
 *    element mounted through its exit animation, then drops it. This is also
 *    the prerequisite for the shared-element poster morph (ui-ux.md §4): both
 *    the leaving grid and the entering detail page must be mounted at once
 *    inside this AnimatePresence for layoutId={`poster-${id}`} to animate.
 *  - Each cached element keeps the location it last rendered with. Without
 *    that, the exiting page's useSearchParams/useLocation read the NEW URL
 *    while its useParams keep the old match: a leaving /show/<show id> saw
 *    the entering movie's ?type=movie and fetched the show id as a movie
 *    (prod catalog gained unrelated on-demand MOVIE# rows that way).
 *  - initial={false} so React's StrictMode double-mount doesn't double-play
 *    the enter animation.
 *  - Scroll follows the browser's model (ui-ux.md §4): a PUSH/REPLACE to a
 *    new pathname starts at the top; Back/Forward (POP) restores the offset
 *    stored for that history entry (`location.key`, mirrored to
 *    sessionStorage so reload-then-Back works); a query-string-only change
 *    keeps the position. `history.scrollRestoration = 'manual'` so the
 *    browser doesn't fight it. window.scrollTo is guarded for environments
 *    that lack it.
 *  - pageVariants() (src/lib/motion.ts) collapses to a bare ≤100ms fade under
 *    prefers-reduced-motion — nothing here bypasses that.
 *  - The exiting route is `inert` from the moment it starts leaving. popLayout
 *    lays it over the entering page, so on a busy main thread (a long season
 *    rendering) a stalled exit otherwise ate clicks: dead taps, or a tap on
 *    the old page's poster opening an unrelated title.
 */
export function PageTransition() {
  const location = useLocation();
  const outlet = useOutlet();
  const navigationType = useNavigationType();
  const { pathname } = location;

  // Last-rendered outlet per pathname, most-recently-used last. Capped at 2
  // entries: the current route plus the one finishing its exit. Re-inserting
  // on revisit keeps recency correct so trimming never evicts the leaving
  // element early.
  const cacheRef = useRef<Map<string, ReactElement>>(new Map());
  const cache = cacheRef.current;
  cache.delete(pathname);
  cache.set(pathname, <LocationContext.Provider value={{ location, navigationType }}>{outlet}</LocationContext.Provider>);
  while (cache.size > 2) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }

  useScrollRestoration(location.key, pathname);

  return (
    // The fallback is the reserved, empty slot (ui-ux.md §7 loading timeline):
    // a contained shimmer box here would flash ahead of the screen's own
    // skeleton (the title page's full-bleed stage) and jump when it swapped.
    <Suspense fallback={<div aria-hidden="true" className="h-[60dvh]" />}>
      <AnimatePresence mode="popLayout" initial={false}>
        <RouteLayer key={pathname}>{cache.get(pathname)}</RouteLayer>
      </AnimatePresence>
    </Suspense>
  );
}

/**
 * The animated route layer. The moment it starts leaving, the layer ITSELF is
 * inert and click-through: popLayout positions it absolutely over the entering
 * page, and an inert child alone is not enough — clicks on an inert node fall
 * to its nearest non-inert ancestor, which was this full-size layer. An exit
 * that runs late (busy main thread, a throttled background tab) then left an
 * invisible page eating every click on the screen below it.
 * `ref` is forwarded (React 19 ref-as-prop) because popLayout measures it.
 */
function RouteLayer({ children, ref }: { children: ReactNode; ref?: Ref<HTMLDivElement> }) {
  const isPresent = useIsPresent();
  return (
    <motion.div
      ref={ref}
      variants={pageVariants()}
      initial="initial"
      animate="animate"
      exit="exit"
      inert={!isPresent}
      style={isPresent ? undefined : { pointerEvents: 'none' }}
    >
      {children}
    </motion.div>
  );
}

// --- Scroll restoration ------------------------------------------------------

const SCROLL_STORE = 'screenatlas:scroll';
const MAX_ENTRIES = 50;
/** Bounded: a restore waits at most this long for the page to grow tall enough. */
const RESTORE_MS = 1000;
/**
 * React Router gives the first entry of every page load this same key (and a
 * POP navigation type), so an offset stored under it would leak into any
 * fresh load of any URL. That entry is instead keyed by an id written into its
 * own `history.state`, which survives reload and Back but not a new load.
 */
const INITIAL_KEY = 'default';
const ENTRY_ID_FIELD = 'saScrollId';

function entryKey(key: string): string {
  if (key !== INITIAL_KEY) return key;
  try {
    const state = (history.state ?? {}) as Record<string, unknown>;
    let id = state[ENTRY_ID_FIELD];
    if (typeof id !== 'string') {
      id = Math.random().toString(36).slice(2);
      history.replaceState({ ...state, [ENTRY_ID_FIELD]: id }, '');
    }
    return `${INITIAL_KEY}:${id as string}`;
  } catch {
    return INITIAL_KEY; // no usable history state: never recorded or restored
  }
}

function loadPositions(): Map<string, number> {
  try {
    return new Map(JSON.parse(sessionStorage.getItem(SCROLL_STORE) ?? '[]') as [string, number][]);
  } catch {
    return new Map();
  }
}

function savePositions(positions: Map<string, number>) {
  try {
    sessionStorage.setItem(SCROLL_STORE, JSON.stringify([...positions].slice(-MAX_ENTRIES)));
  } catch {
    // Storage blocked/full: restoration still works in-session from the Map.
  }
}

function scrollToY(y: number) {
  if (typeof window.scrollTo === 'function') window.scrollTo(0, y);
}

/** window.scrollY per history entry, shared across PageTransition mounts. */
const positions = loadPositions();

function useScrollRestoration(key: string, pathname: string) {
  const navigationType = useNavigationType();
  const keyRef = useRef(entryKey(key));
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    // Passive offset bookkeeping only — no layout reads of any element.
    const onScroll = () => {
      if (keyRef.current !== INITIAL_KEY) positions.set(keyRef.current, window.scrollY);
    };
    const onHide = () => savePositions(positions);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onHide);
    };
  }, []);

  // Layout effect: settle the offset before the entering page paints.
  useLayoutEffect(() => {
    const pathnameChanged = pathnameRef.current !== pathname;
    const entry = entryKey(key);
    keyRef.current = entry;
    pathnameRef.current = pathname;
    savePositions(positions);

    const stored = entry === INITIAL_KEY ? undefined : positions.get(entry);
    if (navigationType === 'POP' && stored !== undefined) {
      // The entering element may still be filling in (cached query data,
      // images): retry each frame until the page is tall enough, bounded.
      const deadline = performance.now() + RESTORE_MS;
      let frame = 0;
      const step = () => {
        scrollToY(stored);
        if (Math.abs(window.scrollY - stored) > 1 && performance.now() < deadline) {
          frame = requestAnimationFrame(step);
        }
      };
      // Never fight the user: any scroll input ends the restore.
      const stop = () => cancelAnimationFrame(frame);
      const inputs = ['wheel', 'touchstart', 'keydown'] as const;
      inputs.forEach((e) => window.addEventListener(e, stop, { once: true, passive: true }));
      step();
      return () => {
        stop();
        inputs.forEach((e) => window.removeEventListener(e, stop));
      };
    }
    if (pathnameChanged) scrollToY(0);
    return undefined;
  }, [key, pathname, navigationType]);
}
